// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// Server-authoritative access gate for the Investment Reporting island. Mirrors
// useResearchHubAccess/useSurfaceAccess: a thin useQuery over the can_review_reporting()
// RPC (Investment ∪ Operations ∪ admins), so the client gate is CONGRUENT with the RLS.
//
// THREE ANSWERS, NOT ONE, because the server asks three different questions. `canReview` and
// `canSign` are the same server predicate (see below). `isInvestmentOrOps` is now only the
// YARDENI predicate: after Sam's two rulings of 2026-08-17 (migration 0003s) every write RPC on
// the reporting island proper admits admins, and the four yardeni_* RPCs on the market-context
// island are the only ones left that do not. `isAdmin` is NOT an access answer at all: it stopped
// being a write restriction when admins were let in, and it survives only so the Gate tab can say
// what a self-signature will RECORD in the append-only log. Never gate a control on `isAdmin`;
// gate it on whichever of the other two the target RPC actually reads.
//
// This deliberately does NOT use requiredTeam="Investment" — that coarse gate wrongly
// excludes Operations (the RLS predicate is Investment ∪ Operations ∪ admins). The island
// wraps in a bare auth-only ProtectedRoute and gates on THIS hook instead.
//
// NOTE: can_review_reporting() is a DRAFT RPC (not yet applied); the supabase client's
// generated types don't know it, so the rpc call is cast through `any`. Regen types.ts
// after 0003c applies and the cast can drop.

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ReportingAccess {
  loading: boolean;
  canReview: boolean; // read access (Investment ∪ Operations ∪ admin) — congruent with RLS
  // profiles.role_level IN ('admin','superadmin'). NOT a write restriction, and not an access
  // answer. Its one legitimate use is telling a signer, before the click, that four eyes is
  // WAIVED for them and their signature will be recorded as 'waived_admin' rather than 'verified'.
  isAdmin: boolean;
  // MAY SIGN THE GATE, AND NOW MAY DRIVE EVERY OTHER reporting_* WRITE. Since the CIO ruling of
  // 2026-08-17 (0003r) the signing predicate in approve_reporting_gate is Investment OR
  // Operations OR admin/superadmin, which is EXACTLY can_review_reporting()'s predicate; since
  // Sam's two rulings of the same day (0003s) that is also the predicate of every other write
  // RPC on this island. So the write audience now equals the read audience, and this is
  // deliberately the same server answer as canReview rather than a second derivation: one
  // source, no drift. The RPC is still the wall (it refuses on reconciliation, flags,
  // render-binding, a NULL uid, and, for a non-admin, four eyes).
  canSign: boolean;
  // MAY DRIVE THE Investment/Ops-ONLY WRITES. This list is now SHORT. Sam's two rulings of
  // 2026-08-17 (migration 0003s) widened every reporting_* write RPC to admins:
  // create_reporting_cycle, advance_reporting_stage, reopen_reporting_cycle,
  // restart_reporting_cycle(_preview), record_newsletter_content_review AND
  // resolve_reporting_flag, which the second ruling reached by waiving four eyes for admins.
  // enqueue_reporting_cycle always admitted them (its predicate is can_review_reporting()).
  //
  // What is STILL narrower, measured live after 0003s: the yardeni RPCs only.
  // yardeni_curate_and_publish, yardeni_verify_chart_read, yardeni_import_top_findings and
  // yardeni_register_chartbook_edition each still open with
  //   IF NOT (public.is_investment_team() OR public.is_operations_team())
  // Neither ruling was about the market-context island, so they were left alone. YardeniCurate
  // is the one surface that should gate on this. Everything in the reporting island proper
  // should gate on canSign.
  isInvestmentOrOps: boolean;
}

export function useReportingAccess(): ReportingAccess {
  const { staffMember } = useAuth();
  const isAdmin = staffMember?.role_level === "admin" || staffMember?.role_level === "superadmin";
  // profiles.team, the exact column is_investment_team()/is_operations_team() read. Measured
  // distinct values: 'Investment', 'Operations', 'Advisory', NULL.
  const isInvestmentOrOps = staffMember?.team === "Investment" || staffMember?.team === "Operations";

  const q = useQuery({
    queryKey: ["can_review_reporting"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("can_review_reporting", {});
      if (error) throw error;
      return Boolean(data);
    },
  });

  return {
    loading: q.isLoading,
    canReview: Boolean(q.data),
    isAdmin,
    canSign: Boolean(q.data),
    isInvestmentOrOps,
  };
}
